// @ts-nocheck
/**
 * SalesforceActivityTab — Salesforce Lightning-style Activity timeline.
 *
 * Matches the SF "All Activity" section with:
 * - Toolbar: action buttons (New Task, New Event, Log a Call, Email) + Filters bar
 * - "Upcoming & Overdue" collapsible section
 * - Monthly grouped timeline with SF-style activity items
 */
import React, { useState, useMemo, useEffect } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Mail,
  Phone,
  Calendar,
  CheckSquare,
  FileText,
  Settings,
  Clock,
  RefreshCw,
  Rows,
  Eye,
} from 'lucide-react';
import { useActivities } from '../../hooks/useActivities';
import type { Activity, RecordType } from '../../types/activity';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface SalesforceActivityTabProps {
  recordId?: string;
  recordType?: RecordType;
  contactName?: string;
  onLogActivity?: (type: 'call' | 'meeting' | 'task' | 'note' | 'email') => void;
  activities?: Activity[];
  loading?: boolean;
  disableQueryFetch?: boolean;
}

/* ------------------------------------------------------------------ */
/*  Icon for activity type                                             */
/* ------------------------------------------------------------------ */

function ActivityIcon({ type }: { type: string }) {
  switch (type) {
    case 'email':
      return (
        <span className="flex items-center justify-center w-7 h-7 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
          <Mail className="w-3.5 h-3.5" />
        </span>
      );
    case 'call':
      return (
        <span className="flex items-center justify-center w-7 h-7 rounded-full bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400">
          <Phone className="w-3.5 h-3.5" />
        </span>
      );
    case 'meeting':
    case 'event':
      return (
        <span className="flex items-center justify-center w-7 h-7 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400">
          <Calendar className="w-3.5 h-3.5" />
        </span>
      );
    case 'task':
      return (
        <span className="flex items-center justify-center w-7 h-7 rounded-full bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400">
          <CheckSquare className="w-3.5 h-3.5" />
        </span>
      );
    default:
      return (
        <span className="flex items-center justify-center w-7 h-7 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
          <FileText className="w-3.5 h-3.5" />
        </span>
      );
  }
}

/* ------------------------------------------------------------------ */
/*  Format helpers                                                     */
/* ------------------------------------------------------------------ */

function fmtTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function fmtShortDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtMonthYear(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

function yearLabel(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const thisYear = now.getFullYear();
  const actYear = d.getFullYear();
  if (actYear === thisYear) return 'This Year';
  if (actYear === thisYear - 1) return 'Last Year';
  return String(actYear);
}

function actionVerb(a: Activity, contactName?: string): string {
  const contact = contactName || 'Contact';
  const user =
    String(a.userName ?? '').trim() ||
    String((a as Activity & { createdByName?: string }).createdByName ?? '').trim() ||
    String((a as Activity & { createdBy?: string }).createdBy ?? '').trim() ||
    'Someone';
  switch (a.type) {
    case 'email':
      return `${a.metadata?.fromEmail || user} sent an email to ${contact}`;
    case 'call':
      return `${user} had a task with ${contact}`;
    case 'task':
      return `${user} had a task with ${contact}`;
    case 'meeting':
    case 'event':
      return `${user} had an event with ${contact}`;
    case 'note':
      return `${user} added a note`;
    default:
      return `${user} logged an activity`;
  }
}

/* ------------------------------------------------------------------ */
/*  Group activities by month                                          */
/* ------------------------------------------------------------------ */

function groupByMonth(activities: Activity[]): Map<string, Activity[]> {
  const groups = new Map<string, Activity[]>();
  const sorted = [...activities].sort((a, b) =>
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  for (const a of sorted) {
    const d = new Date(a.createdAt);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const existing = groups.get(key) ?? [];
    groups.set(key, [...existing, a]);
  }
  return groups;
}

function isUpcomingOrOverdue(a: Activity): boolean {
  if (a.type !== 'task' && a.type !== 'meeting' && a.type !== 'event') return false;
  const status = (a.status ?? '').toLowerCase();
  if (status === 'completed' || status === 'done') return false;
  return true;
}

/* ------------------------------------------------------------------ */
/*  Activity item                                                      */
/* ------------------------------------------------------------------ */

const ActivityItem: React.FC<{
  activity: Activity;
  contactName?: string;
}> = ({ activity, contactName }) => {
  const [expanded, setExpanded] = useState(false);
  const a = activity;
  const title = a.subject || a.title || a.type;
  const dateStr = `${fmtTime(a.createdAt)} | ${fmtShortDate(a.createdAt)}`;
  const emailStatus = a.type === 'email' ? (a.metadata?.opened ? `Last opened ${fmtShortDate(a.metadata.openedAt || a.createdAt)}` : 'Unopened') : null;

  return (
    <div className="flex items-start gap-2 py-3 border-b border-gray-100 dark:border-gray-700/50 last:border-0">
      {/* Expand chevron */}
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 mt-0.5 flex-shrink-0"
      >
        {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
      </button>

      {/* Icon */}
      <ActivityIcon type={a.type} />

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            {a.type === 'task' && (
              <input type="checkbox" className="mr-2 rounded border-gray-300 dark:border-gray-600" checked={a.status === 'completed'} readOnly />
            )}
            <span className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline cursor-pointer">
              {title}
            </span>
            {a.type === 'email' && a.metadata?.hasAttachment && (
              <span className="ml-1 text-gray-400">🔗</span>
            )}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">{dateStr}</span>
            <button type="button" className="p-0.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400">
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
        <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
          {actionVerb(a, contactName)}
        </p>
        {emailStatus && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 flex items-center gap-1">
            <Mail className="w-3 h-3" /> {emailStatus}
          </p>
        )}

        {/* Expanded details */}
        {expanded && a.description && (
          <div className="mt-2 text-xs text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/30 rounded p-3 whitespace-pre-wrap">
            {a.description}
          </div>
        )}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Toolbar button with dropdown                                       */
/* ------------------------------------------------------------------ */

const ToolbarButton: React.FC<{
  icon: React.ReactNode;
  bgColor: string;
  label: string;
  onClick?: () => void;
  dropdownItems?: { label: string; onClick: () => void }[];
}> = ({ icon, bgColor, label, onClick, dropdownItems }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative inline-flex">
      <button
        type="button"
        onClick={onClick ?? (() => setOpen((o) => !o))}
        className={`inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md ${bgColor} text-white text-xs font-medium shadow-sm hover:brightness-95 transition`}
        title={label}
      >
        {icon}
        <span>{label}</span>
      </button>
      {dropdownItems && (
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex items-center justify-center w-5 h-8 rounded-r text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 -ml-0.5"
        >
          <ChevronDown className="w-3 h-3" />
        </button>
      )}
      {open && dropdownItems && (
        <div className="absolute left-0 top-full mt-1 w-36 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded shadow-lg z-20 py-1">
          {dropdownItems.map((item) => (
            <button
              key={item.label}
              type="button"
              onClick={() => { item.onClick(); setOpen(false); }}
              className="w-full text-left px-3 py-1.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

const SalesforceActivityTab: React.FC<SalesforceActivityTabProps> = ({
  recordId,
  recordType = 'client',
  contactName,
  onLogActivity,
  activities: providedActivities,
  loading: providedLoading,
  disableQueryFetch = false,
}) => {
  const fetchEnabled = !disableQueryFetch && !!recordId && !!recordType;
  const queryFilters = fetchEnabled ? { recordId, recordType } : undefined;
  const fetchedState = useActivities(queryFilters, { enabled: fetchEnabled });
  const fetchedLoading = disableQueryFetch ? false : fetchedState.loading;
  const error = disableQueryFetch ? null : fetchedState.error;
  const refetch = disableQueryFetch ? async () => {} : fetchedState.refetch;
  const activities = useMemo(() => {
    const merged = [...(providedActivities ?? []), ...(disableQueryFetch ? [] : fetchedState.activities)];
    const seen = new Set<string>();
    return merged.filter((item) => {
      const key = `${item.id}|${item.type}|${item.createdAt}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [disableQueryFetch, fetchedState.activities, providedActivities]);
  const loading =
    activities.length === 0 &&
    ((typeof providedLoading === 'boolean' && providedLoading) || fetchedLoading);
  const [upcomingCollapsed, setUpcomingCollapsed] = useState(false);
  const [expandAll, setExpandAll] = useState(false);
  const [loadingTimedOut, setLoadingTimedOut] = useState(false);

  useEffect(() => {
    if (!loading) {
      setLoadingTimedOut(false);
      return;
    }
    const timer = window.setTimeout(() => setLoadingTimedOut(true), 12000);
    return () => window.clearTimeout(timer);
  }, [loading, queryFilters?.recordId, queryFilters?.recordType]);

  const upcoming = useMemo(() => activities.filter(isUpcomingOrOverdue), [activities]);
  const past = useMemo(() => activities.filter((a) => !isUpcomingOrOverdue(a)), [activities]);
  const monthGroups = useMemo(() => groupByMonth(past), [past]);

  if (loading && !loadingTimedOut) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          {onLogActivity ? (
            <div className="flex items-center gap-1">
              <ToolbarButton
                icon={<CheckSquare className="w-4 h-4" />}
                bgColor="bg-green-600"
                label="Task"
                dropdownItems={[
                  { label: 'New Task', onClick: () => onLogActivity('task') },
                ]}
                onClick={() => onLogActivity('task')}
              />
              <ToolbarButton
                icon={<Calendar className="w-4 h-4" />}
                bgColor="bg-green-600"
                label="Event"
                dropdownItems={[
                  { label: 'New Event', onClick: () => onLogActivity('meeting') },
                ]}
                onClick={() => onLogActivity('meeting')}
              />
              <ToolbarButton
                icon={<Phone className="w-4 h-4" />}
                bgColor="bg-green-500"
                label="Call"
                onClick={() => onLogActivity('call')}
              />
              <ToolbarButton
                icon={<Mail className="w-4 h-4" />}
                bgColor="bg-gray-500"
                label="Email"
                dropdownItems={[
                  { label: 'New Email', onClick: () => onLogActivity('email') },
                ]}
                onClick={() => onLogActivity('email')}
              />
              <ToolbarButton
                icon={<FileText className="w-4 h-4" />}
                bgColor="bg-blue-500"
                label="Post"
                dropdownItems={[
                  { label: 'New Post', onClick: () => onLogActivity('note') },
                ]}
                onClick={() => onLogActivity('note')}
              />
            </div>
          ) : <div />}

          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
            <span>Filters: All time · All activities · All types</span>
            <button
              type="button"
              onClick={() => { void refetch(); }}
              className="p-1.5 rounded-full bg-gradient-to-r from-blue-600 to-blue-500 text-white hover:from-blue-700 hover:to-blue-600 shadow-sm transition-all"
            >
              <Settings className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2 mt-2 text-xs">
          <button
            type="button"
            onClick={() => { void refetch(); }}
            className="inline-flex items-center gap-1.5 px-2 py-1 rounded border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => setExpandAll((e) => !e)}
            className="inline-flex items-center gap-1.5 px-2 py-1 rounded border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <Rows className="w-3.5 h-3.5" />
            {expandAll ? 'Collapse All' : 'Expand All'}
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-1.5 px-2 py-1 rounded border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <Eye className="w-3.5 h-3.5" />
            View All
          </button>
        </div>
      </div>

      {(error || loadingTimedOut) && (
        <div className="rounded-lg border border-amber-200 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-950/20 px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-amber-800 dark:text-amber-200">
              {error?.message || (loadingTimedOut ? 'Activity feed is taking longer than expected.' : 'Activity feed request failed.')}
            </p>
            <button
              type="button"
              onClick={() => {
                setLoadingTimedOut(false);
                void refetch();
              }}
              className="shrink-0 text-xs font-medium text-amber-700 dark:text-amber-300 hover:underline"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Upcoming & Overdue */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <button
          type="button"
          onClick={() => setUpcomingCollapsed((c) => !c)}
          className="w-full flex items-center gap-2 px-4 py-2.5 bg-gray-50 dark:bg-gray-800/50 text-left"
        >
          {upcomingCollapsed
            ? <ChevronRight className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            : <ChevronDown className="w-4 h-4 text-gray-500 dark:text-gray-400" />}
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">Upcoming &amp; Overdue</span>
        </button>
        {!upcomingCollapsed && (
          <div className="px-4 py-2">
            {upcoming.length === 0 ? (
              <p className="text-xs text-gray-500 dark:text-gray-400 py-3">
                No activities to show. Get started by sending an email, scheduling a task, and more.
              </p>
            ) : (
              <>
                {upcoming.map((a) => (
                  <ActivityItem key={a.id} activity={a} contactName={contactName} />
                ))}
                {upcoming.length > 3 && (
                  <div className="text-center py-2">
                    <button type="button" className="px-3 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">
                      View More
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Monthly timeline groups */}
      {Array.from(monthGroups.entries()).map(([monthKey, items]) => {
        const firstDate = items[0]?.createdAt ?? '';
        return (
          <div key={monthKey}>
            <div className="flex items-center justify-between px-1 mb-2">
              <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-1">
                <ChevronDown className="w-4 h-4 text-gray-400" />
                {fmtMonthYear(firstDate)}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">{yearLabel(firstDate)}</span>
            </div>
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4">
              {items.map((a) => (
                <ActivityItem key={a.id} activity={a} contactName={contactName} />
              ))}
            </div>
          </div>
        );
      })}

      {/* Empty state */}
      {activities.length === 0 && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-12 text-center">
          <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No Activities Yet</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Activities like emails, calls, and meetings will appear here.
          </p>
        </div>
      )}
    </div>
  );
};

export default SalesforceActivityTab;
